import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardContent, Card, Typography, Container, Box, Button, AppBar, Toolbar } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { theme } from '../helpers/colors';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import backgroundImage from '../images/background_airbus.jpg';

const ProviderSelectionPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const handleProviderSelection = (providerKey) => {
    navigate(`/auth/${providerKey}`);
  };

  const handleNext = () => {
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const buttonStyle = {
    width: '254px',
    height: 'auto',
    borderRadius: '16px',
    padding: '15px 20px',
    fontSize: 'clamp(14px, 1vw, 24px)', // This line is updated
    fontWeight: '400',
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
    fontFamily: 'Inter, sans-serif',
  }

  const blackButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'black',
    color: 'white',
  };

  const blueButtonStyle = {
    ...buttonStyle,
    backgroundColor: theme.palette.airbus.main,
    color: 'white',
  };

  const blueNextButtonStyle = {
    ...buttonStyle,
    backgroundColor: theme.palette.airbus.main,
    color: 'white',
    fontSize: 'clamp(12px, 1vw, 24px)',
    width: '150px',
    height: '50px',
    padding: '0 3px',
  };
  const SmallSpacer = () => <div style={{ height: '10px' }} />;

  const ContentMUI = () => {
    return (
      <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 64px)', // Subtract AppBar height
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Card sx={{ 
          minWidth: 300, 
          maxWidth: '90%',
          width: '600px',
          textAlign: 'center', 
          p: 3, 
          position: 'relative',
          backgroundColor: 'white',
        }}>
          {step === 2 && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ position: 'absolute', top: 20, left: 20, zIndex: 9999, }}
              color='airbus'
            >
            </Button>
          )}
          <CardContent sx={{position: 'relative', pb: 7 }}>
            {step === 1 ? (
              <>
                <Typography variant="h4" component="div" gutterBottom sx={{ fontWeight: 'normal', mb: '25px', fontFamily: 'Inter, sans-serif' }}>
                  License Recovery
                </Typography>
                <Typography variant="body1" component="div" gutterBottom sx={{ textAlign:'left',mb:'12px', fontWeight: 'normal', fontFamily: 'Inter, sans-serif' }} color='airbus.light'>
                  <strong>Welcome to the OneLogin License Recovery Page.</strong>
                </Typography>
                <Typography variant="body1" sx={{ textAlign: 'left', mb: '30px', fontFamily: 'Inter, sans-serif', fontSize:'14px' }}>
                  If you are unable to log in to your account due to it being unlicensed, please choose one of the following options:<br/>
                  &#x2022; Select <strong>‘Airbus Group’</strong> for all employes and subcontractors working directly with Airbus divisions and ATR.<br/>
                  &#x2022; Choose the ‘<strong>Customers and Partners</strong>’ for Airbus suppliers and customers using Airbus World and Extended Portals.<br/>
                  <SmallSpacer />
                  For further assistance with license recovery, kindly reach out to your <strong>Airbus Support team</strong>.
                </Typography>
                <Box sx={{ 
                  position: 'absolute', 
                  bottom: 0, 
                  right: 0, 
                  display: 'flex', 
                  justifyContent: 'flex-end'
                }}>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={blueNextButtonStyle}
                  >
                    Next
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="h4" component="div" gutterBottom sx={{ fontWeight: 'normal', mb: '10px', fontFamily: 'Inter, sans-serif' }}>
                  Authentication
                </Typography>
                <Typography variant="body2" sx={{ mb: '20px', fontFamily: 'Inter, sans-serif' }} color='airbus.light'>
                  Please choose your population
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: '5px', sm: '10px' }, justifyContent: 'center' }}>
                  <Button 
                    variant="contained"
                    onClick={() => handleProviderSelection('corpsso')} 
                    sx={blueButtonStyle}
                  >
                  Airbus Group
                  </Button>
                  <Button 
                    variant="contained"
                    onClick={() => handleProviderSelection('mobilesso')} 
                    sx={blackButtonStyle}
                  >
                    Customers and Partners
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
      </ThemeProvider>
    );
  };

  return (
    
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
    <ThemeProvider theme={theme}>
      <AppBar position="static">
      <Container maxWidth="xl">
      <Toolbar disableGutters>
        <svg xmlns="http://www.w3.org/2000/svg" className='airbus-icon' width="126" height="24" viewBox="0 0 640 121.8344">
                <defs id="defs4"></defs>
                <g id="g44809" transform="matrix(5.5695687,0,0,5.5695687,-2558.8034,-4448.4754)">
                    <path d="m 526.05468,815.67559 -7.26563,0 0,-4.07812 7.26953,0 c 1.19532,0.0195 2.16016,0.91406 2.16016,2.03125 0,1.11719 -0.96875,2.04687 -2.16406,2.04687 m -7.26563,-11.95703 7.14063,0 c 0.97656,0.0117 1.80468,0.76563 1.80468,1.75391 0,0.98437 -0.86718,1.76953 -1.84765,1.75781 l -7.09766,0 z m 11.3711,5.70313 c 1.34375,-0.94531 1.85156,-2.51172 1.85156,-4.23438 0,-3.24218 -2.73047,-5.83984 -6.07813,-5.83984 l -12.125,0 0,20.69922 12.60157,0 0,0.008 c 3.53906,0 6.48437,-2.73047 6.48437,-6.07422 0,-2.17969 -1.3125,-3.79297 -2.73437,-4.55859 m 18.69921,-10.1211 0,11.63672 c 0,3.5586 -2.07812,5.05078 -4.63281,5.05078 -2.55859,0 -4.62109,-1.48828 -4.62109,-5.04687 l 0,-11.64063 -5.16016,0 0,11.67188 c 0,7.44531 4.375,9.61328 9.78125,9.61328 5.41016,0 9.78906,-2.16797 9.78906,-9.61328 l 0,-11.67188 z m 18.10547,8.06641 c -4.17578,-0.90625 -5.58593,-1.30859 -5.58593,-2.63281 0,-1.16797 1.51171,-1.75781 3.89843,-1.75781 2.25391,0 4.97657,0.96093 6.57422,1.91796 l 1.62891,-4.21093 c -2.42188,-1.00782 -5.33594,-1.97266 -9.1875,-1.97266 -5.05078,0 -8.20313,2.95313 -8.20313,6.5625 0,3.61328 2.59766,5.20313 7.7461,6.29297 3.99218,0.84375 5.07422,1.35156 5.07422,2.59375 0,1.24219 -1.60157,1.69922 -3.76563,1.69922 -2.80078,0 -5.49609,-0.9336 -8.03516,-2.02344 l -1.53515,4.10528 c 3.03515,1.40625 6.1875,2.53516 9.94531,2.53516 6.26172,0 8.81641,-2.99219 8.81641,-6.48047 0,-3.10157 -2.14844,-5.49219 -7.3711,-6.62891 m -97.08203,-8.01953 -10.45703,20.69531 6.21094,0.004 1.5625,-3.32813 7.13281,0 -2.21875,-4.53515 -2.81641,0 2.65235,-5.66016 6.67969,13.52344 6.22656,0 -10.86328,-20.69922 z m 42.01563,6.88281 c 0,-3.76953 -2.58594,-6.82812 -7.90235,-6.82812 l -10.73828,0 0,20.69922 4.97656,-0.004 0,-16.32422 6.10547,0 c 1.80469,0 2.55469,1.11719 2.55469,2.44531 0,1.33203 -0.78906,2.39844 -2.59375,2.39844 l -4.26953,0 6.46094,11.48047 5.10937,0 c 0,0 -4.42187,-7.50391 -4.39844,-7.50781 3.19141,-0.99219 4.69532,-3.41016 4.69532,-6.35938 m -25.97657,13.82422 5.14844,0 0,-20.69531 -5.14844,0 z" id="path28794" ></path>
                </g>
            </svg>
          
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', ml: 2, flexGrow: 1 }}>
              <Typography variant="h6" noWrap component="div" sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: '.3rem', color: 'inherit', textDecoration: 'none' }}>
              License Recovery
              </Typography>
            </Box>
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            </Box>
          </Toolbar>
          </Container>
      </AppBar>
      </ThemeProvider>
        <ContentMUI />
      </div>
  );
};

export default ProviderSelectionPage;